/* eslint-disable prettier/prettier */
/* eslint-disable react/jsx-props-no-spreading */
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Grid,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as Sentry from "@sentry/react";
import DateRangePicker from "../../../components/common/DateRangePicker/DateRangePicker";
import useQueryParams from "../../../hooks/useQueryParams";
import {
  SET_POSITIONS,
  SET_PROFESSIONS,
} from "../../../redux/actionTypes/meta";
import IntroductionForm from "../../../services/api/introductionForm";
import { isEmptyObj } from "../../../utils/helper";
import { MAINLINE_POSITION_KEY, POSITIONS_KEY } from "./utils";
import { getOptions } from "../../../utils";
import DropDownSelect from "../../../components/common/Dropdown";

const MAINLINE_POSITION = "MAINLINE_POSITION";
const DEPUTY_POSITION = "DEPUTY_POSITION";

const initialFormValue = {
  dateOfBirthFrom: "",
  dateOfBirthTo: "",
  dateOfJoiningFrom: "",
  dateOfJoiningTo: "",
  appointedAtFrom: "",
  appointedAtTo: "",
  conferralDateFrom: "",
  conferralDateTo: "",
  status: false,
  specialGroupAppointments: false,
};

function AdvancedSearch() {
  const [initialAdvanceFormValue, setInitialAdvanceFormValue] =
    useState(initialFormValue);
  const { getQueryObject, getQueryParams, urlSearchParams } = useQueryParams();
  const [examDetailId, setExamDetailId] = useState(null); // ✅ Changed from 0 to null
  const [positions, setPositions] = useState([]);
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [selectedGohonzonTypes, setSelectedGohonzonTypes] = useState([]);
  const gohonzonTypeOptions = [
    { entityTitle: "Okataki gohonzon", value: "OKATAKI_GOHONZON" },
    { entityTitle: "Omamori gohonzon", value: "OMAMORI_GOHONZON" },
  ];
  const ExamDetailId = [
    { entityTitle: "Entrance", value: 52 },
    { entityTitle: "Introductory", value: 51 },
    { entityTitle: "Intermediate", value: 53 },
    { entityTitle: "Advanced", value: 38 },
  ];
  const [profession, setProfession] = useState([]);
  const [selectedProfession, setSelectedProfession] = useState("");
  const [memberStatus, setMemberStatus] = useState(false);
  const [specialGroupAppointments, setSpecialGroupAppointments] =
    useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    const queryObject = getQueryObject();
    if (!isEmptyObj(queryObject)) {
      const query = getQueryParams(queryObject)[0];
      Object.keys(initialFormValue).forEach((key) => {
        setInitialAdvanceFormValue((prev) => ({
          ...prev,
          [key]: query[key] ?? "",
        }));
      });

      [POSITIONS_KEY, MAINLINE_POSITION_KEY].forEach((key) => {
        if (query[key]) {
          const positionsInNumber = query[key].split(",").map(Number);
          if (key === POSITIONS_KEY) setSelectedPositions(positionsInNumber);
        } else if (key === POSITIONS_KEY) setSelectedPositions([]);
      });

      if ("memberStatus" in query && query.memberStatus === "DEACTIVATED") {
        setMemberStatus("DEACTIVATED");
      } else {
        setMemberStatus(false);
      }

      if (
        "specialGroupAppointments" in query &&
        query.specialGroupAppointments === "true"
      ) {
        setSpecialGroupAppointments(true);
      } else {
        setSpecialGroupAppointments(false);
      }

      if ("gohonzonTypes" in query) {
        const gohonzonTypesInString = query.gohonzonTypes.split(",");
        setSelectedGohonzonTypes(gohonzonTypesInString);
      } else {
        setSelectedGohonzonTypes([]);
      }

      // ✅ Profession initialize
      if (query?.professionsId) {
        setSelectedProfession(Number(query.professionsId));
      }

      // ✅ Fixed: null set hoga agar value nahi hai
      setExamDetailId(query?.examDetailId ? Number(query.examDetailId) : null);
    } else {
      // ✅ Clear all states on filter reset
      setInitialAdvanceFormValue(initialFormValue);
      setSelectedPositions([]);
      setMemberStatus(false);
      setSpecialGroupAppointments(false);
      setSelectedGohonzonTypes([]);
      setSelectedProfession("");
      setExamDetailId(null);
    }
  }, [urlSearchParams.toString()]);

  const getPositions = async () => {
    try {
      const [mainlinePositionsRes, othersPositionRes] = await Promise.all([
        IntroductionForm.getAllPositions(MAINLINE_POSITION),
        IntroductionForm.getAllPositions(DEPUTY_POSITION),
      ]);

      const othersFormattedPos =
        othersPositionRes?.data?.map((d) => ({
          entityTitle: d.name,
          value: d.positionId,
        })) ?? [];

      setPositions(othersFormattedPos);

      const allPositions = [
        ...(mainlinePositionsRes?.data || []),
        ...(othersPositionRes?.data || []),
      ].map((d) => ({
        entityTitle: d.name,
        value: d.positionId,
      }));

      if (allPositions.length) {
        dispatch({
          type: SET_POSITIONS,
          payload: allPositions,
        });
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const getProfessions = async () => {
    const response = await IntroductionForm.getProfessions();
    setProfession(response.data);
    dispatch({
      type: SET_PROFESSIONS,
      payload: response.data,
    });
  };

  useEffect(() => {
    getPositions();
    getProfessions();
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        pr: { xs: "20px", sm: 0 },
        // ✅ Global styling - clean and consistent
        "& .MuiInputBase-root": {
          height: "35px !important",
          fontSize: "12px",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
        },
        "& .MuiInputLabel-root": {
          fontSize: "12px !important",
          fontWeight: 600,
          lineHeight: "1",
        },
        "& .MuiInputLabel-shrink": {
          lineHeight: "1.4375em",
        },
        "& .MuiOutlinedInput-input": {
          padding: "7px 14px !important",
          height: "21px !important",
          fontSize: "12px",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderRadius: "8px",
        },
        "& .MuiTextField-root": {
          "& .MuiInputBase-root": {
            height: "35px",
            alignItems: "center",
          },
        },
        "& .MuiAutocomplete-tag": {
          height: "22px",
          fontSize: "11px",
          margin: "2px",
        },
        "& .MuiAutocomplete-input": {
          padding: "0px !important",
        },
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "repeat(3, 1fr)",
          },
          rowGap: 2,
          columnGap: 2,
        }}
      >
        {/* Group 1 */}
        <Grid
          item
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            padding: 1,
            boxShadow: "1px 0 2px 0 #e7e2e2",
          }}
        >
          <DateRangePicker
            label="Date of Birth"
            name="dateOfBirth"
            startDateVal={initialAdvanceFormValue.dateOfBirthFrom}
            endDateVal={initialAdvanceFormValue.dateOfBirthTo}
          />
          <DateRangePicker
            label="Date of Joining"
            name="dateOfJoining"
            startDateVal={initialAdvanceFormValue.dateOfJoiningFrom}
            endDateVal={initialAdvanceFormValue.dateOfJoiningTo}
          />
          <DateRangePicker
            label="Appointment Date"
            name="appointedAt"
            startDateVal={initialAdvanceFormValue.appointedAtFrom}
            endDateVal={initialAdvanceFormValue.appointedAtTo}
          />
        </Grid>

        {/* Group 2 */}
        <Grid
          item
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            padding: 1,
            boxShadow: "1px 0 2px 0 #e7e2e2",
          }}
        >
          <Autocomplete
            options={getOptions(profession, "professionsId")}
            getOptionLabel={(option) => option.profession || ""}
            value={
              profession.find((p) => p.professionsId === selectedProfession) ||
              null
            }
            onChange={(event, newValue) => {
              setSelectedProfession(newValue?.professionsId || "");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Profession"
                size="small"
                placeholder="Select..."
              />
            )}
          />
          {/* ✅ Only add hidden input if profession is selected */}
          {selectedProfession && (
            <input
              type="hidden"
              name="professionsId"
              value={selectedProfession}
            />
          )}

          {/* Other Positions */}
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={positions}
            getOptionLabel={(option) => option.entityTitle}
            value={positions.filter((pos) =>
              selectedPositions.includes(pos.value)
            )}
            onChange={(event, newValue) => {
              const selectedValues = newValue.map((item) => item.value);
              setSelectedPositions(selectedValues);
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  style={{ marginRight: 5, padding: 0 }}
                  checked={selected}
                  size="small"
                />
                {option.entityTitle}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.value}
                  label={option.entityTitle}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Other Positions"
                placeholder="Select..."
                size="small"
              />
            )}
            sx={{
              "& .MuiAutocomplete-inputRoot": {
                paddingTop: selectedPositions.length === 0 ? "0px" : "2px",
              },
            }}
          />
          {/* ✅ Only add hidden input if positions are selected */}
          {selectedPositions.length > 0 && (
            <input
              type="hidden"
              name="dualPositionIds"
              value={selectedPositions.join(",")}
            />
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            <Checkbox
              name="specialGroupAppointments"
              value={specialGroupAppointments}
              checked={specialGroupAppointments}
              onChange={(e) => setSpecialGroupAppointments(e.target.checked)}
              sx={{
                padding: "0px",
              }}
            />
            <span>Special Group Appointments</span>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            <Checkbox
              name="memberStatus"
              value={memberStatus}
              checked={memberStatus}
              onChange={(e) =>
                setMemberStatus(e.target.checked ? "DEACTIVATED" : false)
              }
              sx={{
                padding: "0px",
              }}
            />
            <span>Deactivated</span>
          </Box>
        </Grid>

        {/* Group 3 */}
        <Grid
          item
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            padding: 1,
            boxShadow: "1px 0 2px 0 #e7e2e2",
          }}
        >
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={gohonzonTypeOptions}
            getOptionLabel={(option) => option.entityTitle}
            value={gohonzonTypeOptions.filter((opt) =>
              selectedGohonzonTypes.includes(opt.value)
            )}
            onChange={(event, newValue) => {
              const selected = newValue.map((item) => item.value);
              setSelectedGohonzonTypes(selected);
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  style={{ marginRight: 5, padding: 0 }}
                  checked={selected}
                  size="small"
                />
                {option.entityTitle}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.value}
                  label={option.entityTitle}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Gohonzon Type"
                placeholder="Select..."
                size="small"
              />
            )}
            sx={{
              "& .MuiAutocomplete-inputRoot": {
                paddingTop: selectedGohonzonTypes.length === 0 ? "0px" : "2px",
              },
            }}
          />
          {/* ✅ Only add hidden input if gohonzon types are selected */}
          {selectedGohonzonTypes.length > 0 && (
            <input
              type="hidden"
              name="gohonzonTypes"
              value={selectedGohonzonTypes.join(",")}
            />
          )}

          <DateRangePicker
            label="Conferral Date"
            name="conferralDate"
            startDateVal={initialAdvanceFormValue.conferralDateFrom}
            endDateVal={initialAdvanceFormValue.conferralDateTo}
            PopperProps={{
              modifiers: [
                {
                  name: "preventOverflow",
                  options: {
                    boundary: "viewport",
                  },
                },
              ],
            }}
          />

          {/* ✅ Fixed Exam Level Dropdown */}
          <DropDownSelect
            title="Exam Level Cleared"
            name="examDetailId" // ✅ Keep original name
            selectOptions={ExamDetailId}
            value={examDetailId || ""}
            setValue={(v) => setExamDetailId(v ? Number(v) : null)}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default AdvancedSearch;
